import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { PlatformViewStore } from "../../store";
import { PlatformView } from "../view/PlatformView";
import { PlatformUpdateStore } from "../../store";
import { PlatformUpdate } from "../update/PlatformUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type PlatformModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const PlatformModalContent: React.FC<PlatformModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new PlatformUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new PlatformViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <PlatformView
            platformViewStore={storeInfo.store as PlatformViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <PlatformUpdate
            platformUpdateStore={storeInfo.store as PlatformUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
